/**
 * buildHandlePermissionResponse · Builder function for the permission-response flow.
 *
 * Byte-verbatim extraction of the 267-line handlePermissionResponse function
 * from KnowledgeGraphPage.jsx (pre-extraction lines 835-1101). Zero behaviour
 * change. Every axios URL preserved. Every toast copy preserved. Handles the
 * two branches: 'enable' (grant permission then re-query with location data)
 * and 'search-without' (re-query bypassing the permission check).
 *
 * Extracted 2026-08-01 · IJ-246 · Phase 5D-2 of the KnowledgeGraphPage.jsx
 * split (see /app/memory/design_drafts/kg_page_split_map.md Section Three
 * Boundary Three · sub-slice 5D-2).
 * Umbrella: IJ-076 Predictive Text Rebuild continuation · Refactor
 * Programme 2026.
 * Owner: E1.
 *
 * Pattern: buildHandlePermissionResponse(deps) returns an async function.
 */
import { API, authAxios } from '../../utils/authClient';
import { toast } from 'sonner';

export function buildHandlePermissionResponse(deps) {
  const {
    // State reads
    dataControls, accuracyMode, queryThread, quietMode,
    // Setters
    setDataControls, setAccuracyScore, setIsQuerying, setQueryThread,
    // Parent-owned helpers
    requestGeolocation, scrollToThreadItem, handleQuotaExceededError,
  } = deps;

  // Instantiate the authed axios client once. authAxios is a factory; calling
  // it returns an axios instance with the Bearer-token interceptor wired in.
  // Fixes 'api.post is not a function' crash — same regression as
  // useUploadFlow.js line 45. Root-caused 2026-08-11 during upload sweep.
  const api = authAxios();

  const handlePermissionResponse = async (action, permissionsNeeded, originalQuery, kgRelevant) => {
    if (action === 'enable') {
      // Enable the required permissions
      const newControls = { ...dataControls };
      const needsLocation = permissionsNeeded.some(p => p.type === 'location');
      for (const perm of permissionsNeeded) {
        const key = perm.type === 'username_data' ? 'username' : perm.type;
        newControls[key] = 'on';
      }
      setDataControls(newControls);
      
      // Save preferences to backend
      try {
        const r = await api.post(`${API}/user/preferences`, {
          username_data: newControls.username,
          location: newControls.location,
          social: newControls.social,
          media: newControls.media,
          accuracy_mode: accuracyMode,
        });
        setAccuracyScore(r.data.accuracy_score);
        toast.success('Permissions enabled — getting your location...');
      } catch (e) {
        console.error('Failed to enable permissions:', e);
        toast.error('Failed to enable permissions');
        return;
      }
      
      // Request geolocation IMMEDIATELY if location permission was just enabled
      // (Cannot rely on handleQuery because of React stale closure — dataControls.location is still 'off' there)
      let locationData = null;
      if (needsLocation) {
        locationData = await requestGeolocation();
        if (locationData) {
          toast.success(`Location: ${locationData.display || 'detected'}`);
        } else {
          toast.error('Could not access your location. Please allow browser location access and try again.');
        }
      }
      
      // Submit query directly with location data (bypass handleQuery stale closure)
      setIsQuerying(true);
      const conversation_history = queryThread
        .filter(item => item.result && item.result.type !== 'permission_required')
        .map(item => ({ query: item.query, answer: item.result.answer, source_documents: item.result.source_documents || [] }));
      
      // Replace the clarification prompt with a loading state (don't add new entry)
      let replacedIdx = -1;
      setQueryThread(prev => {
        const updated = [...prev];
        const idx = updated.findIndex(item => 
          item.result && item.result.type === 'permission_required' && item.result.original_query === originalQuery
        );
        if (idx >= 0) {
          replacedIdx = idx;
          updated[idx] = {
            query: originalQuery,
            result: {
              answer: locationData ? `*Searching near ${locationData.display}...*` : '*Searching...*',
              source_entities: [], source_documents: [], related_concepts: [],
              images: [], suggestions: [], version: '', citation_map: {},
              web_results: [], web_intro: '', has_kg_content: false
            },
            isStreaming: true
          };
        }
        return updated;
      });
      scrollToThreadItem(replacedIdx >= 0 ? replacedIdx : 0);
      
      try {
        const token = localStorage.getItem('startech_token');
        const queryBody = {
          query: originalQuery,
          query_type: 'general',
          conversation_history,
          skip_permission_check: true, // We just enabled the permission
          skip_kg: !kgRelevant,
          quiet_mode: quietMode,
        };
        if (locationData && locationData.lat) {
          queryBody.user_location = {
            lat: locationData.lat, lng: locationData.lng,
            suburb: locationData.suburb || '',
            city: locationData.city || '', region: locationData.region || '',
            country: locationData.country || '', display: locationData.display || '',
          };
        }
        
        const startResp = await fetch(`${API}/query/start`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify(queryBody)
        });
        if (!startResp.ok) {
          // 402 Beta quota — fire Exit Banner cleanly.
          if (startResp.status === 402) {
            let qErr = {};
            try { qErr = await startResp.json(); } catch {}
            handleQuotaExceededError({ response: { status: 402, data: qErr } });
            return;
          }
          throw new Error('Failed to start query');
        }
        const { job_id } = await startResp.json();
        
        // Poll for results
        const maxPollTime = 300000;
        const pollInterval = 3000;
        const startTime = Date.now();
        let data = null;
        
        while (Date.now() - startTime < maxPollTime) {
          await new Promise(r => setTimeout(r, pollInterval));
          const pollResp = await fetch(`${API}/query/result/${job_id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (!pollResp.ok) continue;
          const pollData = await pollResp.json();
          if (pollData.status === 'complete') { data = pollData.result; break; }
          if (pollData.status === 'error') throw new Error(pollData.error);
        }
        if (!data) throw new Error('Query timed out');
        
        const completeResult = {
          answer: data.answer || '', source_entities: data.source_entities || [],
          source_documents: data.source_documents || [], related_concepts: data.related_concepts || [],
          images: data.images || [], suggestions: data.suggestions || [],
          version: data.version || '', citation_map: data.citation_map || {},
          web_results: data.web_results || [], web_intro: data.web_intro || '',
          has_kg_content: data.has_kg_content || false
        };
        
        setQueryThread(prev => {
          const updated = [...prev];
          const idx = updated.findIndex(item => item.query === originalQuery && item.isStreaming);
          if (idx >= 0) updated[idx] = { query: originalQuery, result: completeResult, isStreaming: false };
          return updated;
        });
        toast.success('Query completed');
      } catch (error) {
        toast.error(error.message || 'Query failed');
        setQueryThread(prev => {
          const updated = [...prev];
          const idx = updated.findIndex(item => item.query === originalQuery && item.isStreaming);
          if (idx >= 0) updated[idx] = {
            query: originalQuery,
            result: { answer: `Error: ${error.message}`, source_entities: [], source_documents: [], related_concepts: [], images: [], suggestions: [], version: '' },
            isStreaming: false
          };
          return updated;
        });
      }
      setIsQuerying(false);
    } else {
      // "Search Without" — re-run query bypassing permission check and skipping KG if not relevant
      // Replace the permission prompt with a loading state
      setQueryThread(prev => {
        const updated = [...prev];
        const idx = updated.findIndex(item => 
          item.result && item.result.type === 'permission_required' && item.result.original_query === originalQuery
        );
        if (idx >= 0) {
          updated[idx] = {
            query: originalQuery,
            result: {
              answer: '*Searching web sources...*',
              source_entities: [], source_documents: [], related_concepts: [],
              images: [], suggestions: [], version: '', citation_map: {},
              web_results: [], web_intro: '', has_kg_content: false
            },
            isStreaming: true
          };
        }
        return updated;
      });
      
      setIsQuerying(true);
      const conversation_history = queryThread
        .filter(item => item.result && item.result.type !== 'permission_required')
        .map(item => ({ query: item.query, answer: item.result.answer, source_documents: item.result.source_documents || [] }));
      
      try {
        const token = localStorage.getItem('startech_token');
        const startResp = await fetch(`${API}/query/start`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({
            query: originalQuery,
            query_type: 'general',
            conversation_history,
            skip_permission_check: true,
            skip_kg: !kgRelevant,
            quiet_mode: quietMode,
          })
        });
        
        if (!startResp.ok) {
          // 402 Beta quota — fire Exit Banner cleanly.
          if (startResp.status === 402) {
            let qErr = {};
            try { qErr = await startResp.json(); } catch {}
            handleQuotaExceededError({ response: { status: 402, data: qErr } });
            return;
          }
          throw new Error('Failed to start query');
        }
        const { job_id } = await startResp.json();
        
        // Poll for results
        const maxPollTime = 300000;
        const pollInterval = 3000;
        const startTime = Date.now();
        let data = null;
        
        while (Date.now() - startTime < maxPollTime) {
          await new Promise(r => setTimeout(r, pollInterval));
          const pollResp = await fetch(`${API}/query/result/${job_id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (!pollResp.ok) continue;
          const pollData = await pollResp.json();
          if (pollData.status === 'complete') { data = pollData.result; break; }
          if (pollData.status === 'error') throw new Error(pollData.error);
        }
        
        if (!data) throw new Error('Query timed out');
        
        const completeResult = {
          answer: data.answer || '', source_entities: data.source_entities || [],
          source_documents: data.source_documents || [], related_concepts: data.related_concepts || [],
          images: data.images || [], suggestions: data.suggestions || [],
          version: data.version || '', citation_map: data.citation_map || {},
          web_results: data.web_results || [], web_intro: data.web_intro || '',
          has_kg_content: data.has_kg_content || false
        };
        
        // Update the permission prompt entry with the real result
        setQueryThread(prev => {
          const updated = [...prev];
          const idx = updated.findIndex(item => item.query === originalQuery && item.isStreaming);
          if (idx >= 0) {
            updated[idx] = { query: originalQuery, result: completeResult, isStreaming: false };
          }
          return updated;
        });
        toast.success('Query completed');
      } catch (error) {
        toast.error(error.message || 'Query failed');
        setQueryThread(prev => {
          const updated = [...prev];
          const idx = updated.findIndex(item => item.query === originalQuery && item.isStreaming);
          if (idx >= 0) {
            updated[idx] = {
              query: originalQuery,
              result: { answer: `Error: ${error.message}`, source_entities: [], source_documents: [], related_concepts: [], images: [], suggestions: [], version: '' },
              isStreaming: false
            };
          }
          return updated;
        });
      }
      setIsQuerying(false);
    }
  };

  // Load preferences when component mounts (already authenticated if rendered)

  return handlePermissionResponse;
}

export default buildHandlePermissionResponse;
